import type {
  DesignBody,
  DesignDocument,
  DesignNode,
  ImageNode,
  SizeMm,
  TextNode,
} from '@repo/api-contracts';
import { layoutText, type MeasureText } from '@repo/design-renderer';

export interface FoundNode {
  readonly node: DesignNode;
  readonly componentId: string;
}

const findIn = (nodes: readonly DesignNode[], nodeId: string): DesignNode | null => {
  for (const node of nodes) {
    if (node.id === nodeId) return node;
    if (node.type === 'group') {
      const inner = findIn(node.children, nodeId);
      if (inner !== null) return inner;
    }
  }
  return null;
};

export const findNode = (document: DesignDocument, nodeId: string): FoundNode | null => {
  for (const component of document.components) {
    const node = findIn(component.layers, nodeId);
    if (node !== null) return { node, componentId: component.componentId };
  }
  return null;
};

export const layersOf = (document: DesignDocument, componentId: string): readonly DesignNode[] =>
  document.components.find((component) => component.componentId === componentId)?.layers ?? [];

const mapTree = (
  nodes: readonly DesignNode[],
  fn: (nodes: readonly DesignNode[]) => readonly DesignNode[],
): readonly DesignNode[] =>
  fn(nodes).map((node) =>
    node.type === 'group' ? { ...node, children: [...mapTree(node.children, fn)] } : node,
  );

const mapLayers = (
  document: DesignDocument,
  fn: (nodes: readonly DesignNode[]) => readonly DesignNode[],
): DesignDocument => ({
  ...document,
  components: document.components.map((component) => ({
    ...component,
    layers: [...mapTree(component.layers, fn)],
  })),
});

export const updateNode = (
  document: DesignDocument,
  nodeId: string,
  patch: (node: DesignNode) => DesignNode,
): DesignDocument =>
  mapLayers(document, (nodes) => nodes.map((node) => (node.id === nodeId ? patch(node) : node)));

export const removeNode = (document: DesignDocument, nodeId: string): DesignDocument =>
  mapLayers(document, (nodes) => nodes.filter((node) => node.id !== nodeId));

/** 맨 위(배열 끝)에 넣는다. 컴포넌트 항목이 없으면 만든다. */
export const insertNode = (
  document: DesignDocument,
  componentId: string,
  node: DesignNode,
): DesignDocument => {
  const exists = document.components.some((component) => component.componentId === componentId);
  const components = exists
    ? document.components.map((component) =>
        component.componentId === componentId
          ? { ...component, layers: [...component.layers, node] }
          : component,
      )
    : [...document.components, { componentId, layers: [node] }];
  return { ...document, components };
};

/** 같은 부모 안에서 한 칸 위('up' = 배열 뒤)·아래로 옮긴다. 끝이면 그대로다. */
export const reorderNode = (
  document: DesignDocument,
  nodeId: string,
  direction: 'up' | 'down',
): DesignDocument =>
  mapLayers(document, (nodes) => {
    const index = nodes.findIndex((node) => node.id === nodeId);
    const target = direction === 'up' ? index + 1 : index - 1;
    if (index < 0 || target < 0 || target >= nodes.length) return nodes;
    const copy = [...nodes];
    const [moved] = copy.splice(index, 1);
    if (moved === undefined) return nodes;
    copy.splice(target, 0, moved);
    return copy;
  });

export const emptyDocument = (productId: string, colorOptionId: string): DesignDocument => ({
  productId,
  colorOptionId,
  components: [],
});

/** 새 텍스트 글자 크기 범위(mm) — 아주 작은 영역에서도 읽히고, 큰 영역에서도 한 줄 문구가 화면을 덮지 않게. */
const DEFAULT_TEXT_SIZE_MM = { min: 3, max: 20 } as const;

/**
 * 새 텍스트의 글자 크기 — 인쇄 영역 짧은 변의 1/16 을 0.5mm 단위로 맞추고 3~20mm 로 묶는다.
 * 기본 문구("텍스트")와 15자 안팎의 영문 문구가 영역 폭의 60% 안에 한 줄로 들어오는 크기다.
 */
const defaultTextSizeMm = (component: SizeMm): number => {
  const raw = Math.round((Math.min(component.widthMm, component.heightMm) / 16) * 2) / 2;
  return Math.min(DEFAULT_TEXT_SIZE_MM.max, Math.max(DEFAULT_TEXT_SIZE_MM.min, raw));
};

export const createDefaultTextNode = (input: {
  id: string;
  fontId: string;
  inkColorId: string;
  component: SizeMm;
}): TextNode => {
  const fontSizeMm = defaultTextSizeMm(input.component);
  const width = input.component.widthMm * 0.8;
  const height = fontSizeMm * 1.2;
  return {
    id: input.id,
    type: 'text',
    x: (input.component.widthMm - width) / 2,
    y: (input.component.heightMm - height) / 2,
    rotationDeg: 0,
    width,
    height,
    text: '텍스트를 입력하세요',
    fontId: input.fontId,
    inkColorId: input.inkColorId,
    fontSizeMm,
    bold: false,
    italic: false,
    underline: false,
    strikethrough: false,
    align: 'center',
    letterSpacingEm: 0,
    lineHeight: 1.2,
  };
};

export const createImageNodeFitted = (input: {
  id: string;
  assetId: string;
  widthPx: number;
  heightPx: number;
  component: SizeMm;
}): ImageNode => {
  const maxW = input.component.widthMm * 0.8;
  const maxH = input.component.heightMm * 0.8;
  const ratio = Math.min(maxW / input.widthPx, maxH / input.heightPx);
  const width = input.widthPx * ratio;
  const height = input.heightPx * ratio;
  return {
    id: input.id,
    type: 'image',
    x: (input.component.widthMm - width) / 2,
    y: (input.component.heightMm - height) / 2,
    rotationDeg: 0,
    width,
    height,
    assetId: input.assetId,
    flipX: false,
    flipY: false,
  };
};

/** 텍스트 상자 높이(정책 §3-5: 에디터가 계산해 저장). */
export const textHeightMm = (node: TextNode, familyName: string, measure: MeasureText): number =>
  layoutText(node, familyName, measure).heightMm;

/** 폰트가 아직 없거나 실패하면 쓰는 대체 패밀리. 렌더러도 같은 이름으로 브라우저 대체 글꼴을 쓴다. */
export const FALLBACK_FONT_FAMILY = 'sans-serif';

export type FamilyOf = (fontId: string) => string | undefined;

/** 텍스트 노드의 height 를 현재 폭·글꼴로 다시 계산한다. */
export const sizeTextNode = (
  node: TextNode,
  familyOf: FamilyOf,
  measure: MeasureText,
): TextNode => ({
  ...node,
  height: textHeightMm(node, familyOf(node.fontId) ?? FALLBACK_FONT_FAMILY, measure),
});

/**
 * 캔버스 드래그 결과를 문서에 반영한다. 텍스트는 좌우 변으로 폭만 바뀌므로 height 를 layoutText 로 다시 계산한다(스펙 §6-3).
 */
export const commitNodeChange = (
  document: DesignDocument,
  node: ImageNode | TextNode,
  familyOf: FamilyOf,
  measure: MeasureText,
): DesignDocument => {
  const next = node.type === 'text' ? sizeTextNode(node, familyOf, measure) : node;
  return updateNode(document, node.id, () => next);
};

const collectSorted = (body: DesignBody, pick: (node: DesignNode) => string | null): string[] => {
  const ids = new Set<string>();
  const walk = (nodes: readonly DesignNode[]): void => {
    for (const node of nodes) {
      const id = pick(node);
      if (id !== null) ids.add(id);
      if (node.type === 'group') walk(node.children);
    }
  };
  for (const component of body.components) walk(component.layers);
  return [...ids].sort();
};

/** 본문이 쓰는 폰트 id(그룹 안 포함, 중복 없이 정렬). 폰트 지연 로드의 '필요한 폰트' 다. */
export const collectFontIds = (body: DesignBody): string[] =>
  collectSorted(body, (node) => (node.type === 'text' ? node.fontId : null));

/** 본문이 쓰는 이미지 에셋 id(그룹 안 포함, 중복 없이 정렬). */
export const collectAssetIds = (body: DesignBody): string[] =>
  collectSorted(body, (node) => (node.type === 'image' ? node.assetId : null));

export const collectDocumentAssetIds = (document: DesignDocument): string[] =>
  collectAssetIds({ components: document.components });
